import React, { useCallback, useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { debounce } from "lodash";

import { useLazyLoadQuery } from "~/lib/relayUtil";
import Filter from "~/search/filters/Filter";
import Avatar from "~/ui/avatar";
import Menu from "~/ui/menu";

import type { UserInput } from "./types";

import type { CopilotSettingsUserFilter$key } from "~/__generated__/CopilotSettingsUserFilter.graphql";
import type { CopilotSettingsUserFilterQuery } from "~/__generated__/CopilotSettingsUserFilterQuery.graphql";
import type { CopilotSettingsUserFilterUserOption$key } from "~/__generated__/CopilotSettingsUserFilterUserOption.graphql";

import styles from "~/search/filters/SearchFilters.module.css";

const usersQuery = graphql`
	query CopilotSettingsUserFilterQuery($query: String) {
		selectUsers: users(first: 10, search: $query, isGuests: false) {
			edges {
				node {
					email
					...CopilotSettingsUserFilterUserOption
				}
			}
		}
	}
`;

interface UserOptionProps {
	user: CopilotSettingsUserFilterUserOption$key;
	selectUser: (user: UserNode) => void;
}

type UserNode = NonNullable<UserInput>["user"] | null;

export function UserOption({ user: userRef, selectUser }: UserOptionProps) {
	const userFragment = graphql`
		fragment CopilotSettingsUserFilterUserOption on User {
			id
			email
			image
			fullName
			isGuest
			...CopilotSettingsUserFilter
			...UserSelectionStepGetIncludesString
			...RuleConfigurationStepUserPill
			...UserSelectionStepGetIncludesString
		}
	`;

	const user = useFragment<CopilotSettingsUserFilterUserOption$key>(userFragment, userRef);

	const onSelect = useCallback(() => {
		selectUser(user);
	}, [selectUser, user]);

	return (
		<Menu.Item onSelect={onSelect} className={styles.userFilterItem}>
			<Avatar user={{ ...user, fullName: user.fullName || user.email, image: user.image || undefined }} size="small" />
			{user.fullName || user.email}
		</Menu.Item>
	);
}

interface CopilotSettingsUserFilterProps {
	searchParam: string;
	triggerText: string;
	onSelectUser: (user: UserNode) => void;
	value: UserNode;
}

export default function CopilotSettingsUserFilter({
	searchParam,
	triggerText,
	onSelectUser,
	value: selectedUserRef,
}: CopilotSettingsUserFilterProps) {
	const userFilterFragment = graphql`
		fragment CopilotSettingsUserFilter on User {
			id
			email
			image
			fullName
			isGuest
		}
	`;

	const selectedUser = useFragment<CopilotSettingsUserFilter$key>(userFilterFragment, selectedUserRef);

	const [query, setQuery] = useState<string>();

	const debouncedSearchQuery = useMemo(() => {
		return debounce(value => {
			setQuery(value);
		}, 350);
	}, []);

	const triggerContent = useMemo(() => {
		if (!selectedUser) return triggerText;
		return (
			<div className={styles.triggerContent}>
				<Avatar
					user={{
						...selectedUser,
						fullName: selectedUser.fullName || selectedUser.email,
						image: selectedUser.image || undefined,
					}}
					size="small"
				/>
				<span className={styles.triggerContentText}>{selectedUser.fullName}</span>
			</div>
		);
	}, [selectedUser, triggerText]);

	return (
		<Filter triggerContent={triggerContent} searchParam={searchParam} onSearch={debouncedSearchQuery} hideClearFilter>
			<React.Suspense fallback={<span className={styles.loadingState}>Loading...</span>}>
				<OptionsRenderer query={query} selectUser={onSelectUser} />
			</React.Suspense>
		</Filter>
	);
}

interface UsersRendererProps {
	query?: string;
	selectUser: (user: UserNode) => void;
}

function OptionsRenderer({ query, selectUser }: UsersRendererProps) {
	const data = useLazyLoadQuery<CopilotSettingsUserFilterQuery>(
		usersQuery,
		{ query },
		{ fetchPolicy: "store-or-network" },
	);

	const usersToRender = useMemo(() => {
		return data.selectUsers?.edges.map(edge => edge.node) || [];
	}, [data]);

	if (usersToRender.length === 0) {
		return <span className={styles.emptyState}>No users found.</span>;
	}

	return (
		<>
			{usersToRender.map(user => (
				<UserOption key={user.email} user={user} selectUser={selectUser} />
			))}
		</>
	);
}
